import React, { PropsWithChildren } from 'react';
import Context from '../Contexts';
import { AnyStep } from '..';
import produce from 'immer';
type WizardState = {
  steps: AnyStep[];
  activePage?: string;
  activeGroup?: string;
};
type WizardProps = PropsWithChildren<{
  onComplete: () => void;
}>;

export default class Wizard extends React.Component<WizardProps, WizardState> {
  private setNextActive = (state: WizardState) => {
    const arr = state.steps.filter(x => x.kind === 'step').map(x => x.name);
    const index = arr.findIndex(x => x === state.activePage);
    const name = state.activePage || '';
    if (!this.isLastStep(name, state)) {
      return this.setActive(arr[index + 1], state);
    }
    return state;
  };
  private isLastStep = (name: string, state: WizardState) => {
    const arr = state.steps
      .map(x => x.name)
      .slice()
      .sort();
    const index = arr.findIndex(x => x === name);
    if (index >= arr.length - 1) return true;
    return false;
  };
  private setActive = (name: string, state: WizardState) => {
    return produce(state, draft => {
      draft.steps.forEach(s => {
        if (s.name === name) {
          s.disabled = false;
          s.active = true;
        } else s.active = false;
      });
      draft.activePage = name;
      draft.activeGroup = undefined;
      draft.steps
        .filter(s => s.kind === 'group' && name.startsWith(s.name))
        .forEach(step => {
          const id = draft.steps.findIndex(s => step.name === s.name);
          draft.steps[id].active = true;
          draft.steps[id].disabled = false;
        });
      return draft;
    });
  };

  private setGroup = (name: string, state: WizardState) => {
    state.steps.forEach(s => {
      if (s.name === name) {
        s.disabled = false;
        s.active = !s.active;
      }
    });
    state.activeGroup = state.activeGroup ? undefined : name;
    state.steps
      .filter(s => s.kind === 'group' && name.startsWith(s.name))
      .forEach(step => {
        const id = state.steps.findIndex(s => step.name === s.name);
        state.steps[id].active = true;
        state.steps[id].disabled = false;
      });
    return state;
  };

  constructor(props: WizardProps) {
    super(props);
    this.state = {
      steps: [],
    };
  }

  register = (name: string, step: AnyStep) => {
    this.setState(
      state =>
        produce(state, draft => {
          const index = draft.steps.findIndex(s => s.name === name);
          if (index < 0) {
            draft.steps.push(step);
            return draft;
          }
          draft.steps = draft.steps.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: 'base',
            }),
          );
          return draft;
        }),
      () => {
        if (this.state.activePage === undefined && !!this.state.steps[0]) {
          this.setActivePage(this.state.steps[0].name);
        }
      },
    );
  };

  update = (name: string, step: AnyStep) => {
    this.setState(state =>
      produce(state, draft => {
        const index = draft.steps.findIndex(s => s.name === name);
        if (index < 0) {
          return draft;
        }
        const { title, loading, subTitle } = step;
        draft.steps[index].loading = loading;
        draft.steps[index].title = title;
        draft.steps[index].subTitle = subTitle;
        return draft;
      }),
    );
  };

  onLoad = (name: string) => {
    this.setState(state =>
      produce(state, draft => {
        const index = draft.steps.findIndex(s => s.name === name);
        if (index < 0) {
          return draft;
        }
        draft.steps[index].loading = false;
      }),
    );
  };

  onComplete = (name: string) => {
    this.setState(state => {
      const retV = produce(state, draft => {
        const index = draft.steps.findIndex(s => s.name === name);
        if (index < 0) {
          return draft;
        }
        draft.steps[index].complete = true;

        draft.steps.forEach(s => {
          if (s.activeAfterComplete) s.disabled = false;
        });
        draft.steps
          .filter(s => s.kind === 'group' && name.startsWith(s.name))
          .forEach(step => {
            if (
              draft.steps
                .filter(
                  s => s.name.startsWith(step.name) && s.name !== step.name,
                )
                .every(x => x.complete)
            ) {
              const id = draft.steps.findIndex(s => step.name === s.name);
              draft.steps[id].complete = true;
            } else {
              const id = draft.steps.findIndex(s => step.name === s.name);
              draft.steps[id].complete = false;
            }
          });
        return draft;
      });
      return this.setNextActive(retV);
    });
    if (this.isLastStep(name, this.state) && this.props.onComplete) {
      return this.props.onComplete();
    }
  };
  setActivePage = (name: string) => {
    this.setState(state => this.setActive(name, state));
  };

  setActiveGroup = (name: string) => {
    this.setState(state =>
      produce(state, draft => {
        return this.setGroup(name, draft);
      }),
    );
  };

  render = () => {
    return (
      <Context.Provider
        value={{
          steps: this.state.steps,
          activePage: this.state.activePage,
          activeGroup: this.state.activeGroup,
          onComplete: this.onComplete,
          onLoad: this.onLoad,
          setActivePage: this.setActivePage,
          setActiveGroup: this.setActiveGroup,
          register: this.register,
          update: this.update,
        }}>
        {this.props.children}
      </Context.Provider>
    );
  };
}
